import { ReactElement, Suspense, lazy, useEffect, useState } from 'react';

import { getImageExtensionFromMimeType } from '@utils/assets';
import { getFileExtension } from '@utils/browser';

import { ImageViewer } from './image-viewer';

import { ImageExtension } from '../types/file_extension';

const PDF = lazy(() => import('./PDF'));

export type PreviewerProps = {
  url: string | null;
  fileName?: string;
  mimeType?: string;
  paginate?: boolean;
  maxWidth: number;
  parentId: string;
  fallback: ReactElement;
};

export function Previewer({
  url,
  fileName,
  mimeType,
  paginate = false,
  maxWidth,
  parentId,
  fallback,
}: PreviewerProps) {
  const [pdfBlob, setPdfBlob] = useState<Blob | undefined>(undefined);
  const urlExtension = getFileExtension(url)?.toLowerCase();
  const fileNameExtension = getFileExtension(fileName)?.toLowerCase();
  const mimeTypeExtension =
    mimeType && mimeType.includes('/')
      ? getImageExtensionFromMimeType(mimeType).replace('.', '').toLowerCase()
      : null;
  const isSupportedExtension = (extension: string | null | undefined) =>
    extension != null && (extension === 'pdf' || extension in ImageExtension);
  const fileExtension = isSupportedExtension(urlExtension)
    ? urlExtension
    : fileNameExtension || mimeTypeExtension;

  useEffect(() => {
    if (fileExtension === 'pdf') {
      fetch(url!)
        .then((res) => res.blob())
        .then((blob) => {
          setPdfBlob(blob);
        });
    }
  }, [fileExtension, url]);

  if (!url || !fileExtension) {
    return fallback;
  }
  if (fileExtension === 'pdf') {
    return (
      <Suspense fallback={<div />}>
        <PDF file={pdfBlob} paginate={paginate} maxWidth={maxWidth} parentId={parentId} />
      </Suspense>
    );
  }
  if (fileExtension in ImageExtension) {
    return <ImageViewer url={url} maxWidth={maxWidth} />;
  }
  return fallback;
}

export default Previewer;
